// src/pages/notifications/ContactListPage.tsx
// Personal contacts settings page — card-based layout matching settings design.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsContact, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, Badge, ErrorAlert,
} from '@react/components';
import { formatContactType } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { Contact } from '@core/types/notifications/Contact';

export const ContactListPage = observer(() => {
  const store = useNotificationsContact();
  const { t } = useI18n();

  useEffect(() => {
    store.ListContacts({ limit: 50 } as never);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id: string) => {
    if (!confirm(t('notifications.deleteContactConfirm'))) return;
    try {
      await store.DeleteContact(id);
    } catch {
      // handled by store
    }
  };

  const handleSetPrimary = async (id: string) => {
    try {
      await store.SetContactPrimary(id, {} as never);
      store.ListContacts({ limit: 50 } as never);
    } catch {
      // handled by store
    }
  };

  const handleSendVerification = async (id: string) => {
    try {
      await store.SendContactVerification(id, {} as never);
    } catch {
      // handled by store
    }
  };

  const items = store.items as Contact[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('settings.nav.contacts')}</h1>
        <Link to="/settings/contacts/new" className="ml-auto">
          <Button>{t('notifications.createContact')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('notifications.contacts')}</CardTitle>
          <CardDescription>{t('notifications.contactType')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {store.isLoading && items.length === 0 ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <NotificationIcon name="mail" className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">{t('notifications.noContacts')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('settings.nav.contactsDesc')}</p>
              <Link to="/settings/contacts/new">
                <Button variant="secondary" size="sm" className="mt-3">{t('notifications.createContact')}</Button>
              </Link>
            </div>
          ) : (
            items.map((c) => {
              const ct = formatContactType(c.type ?? '');
              return (
                <div
                  key={c.entity?.id}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <NotificationIcon name={ct.icon} className="w-5 h-5 text-muted-foreground" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{c.value}</span>
                      {c.is_primary && <Badge variant="primary">{t('notifications.contactPrimary')}</Badge>}
                      {c.is_verified ? (
                        <Badge variant="success">{t('notifications.verified')}</Badge>
                      ) : (
                        <Badge variant="warning">{t('notifications.unverified')}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t(ct.labelKey)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!c.is_verified && (
                      <Button variant="ghost" size="sm" onClick={() => handleSendVerification(c.entity?.id ?? '')}>
                        {t('notifications.verify')}
                      </Button>
                    )}
                    {!c.is_primary && c.is_verified && (
                      <Button variant="ghost" size="sm" onClick={() => handleSetPrimary(c.entity?.id ?? '')}>
                        {t('notifications.setPrimary')}
                      </Button>
                    )}
                    <Link to={`/settings/contacts/${c.entity?.id}/edit`}>
                      <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                    </Link>
                    {!c.is_primary && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.entity?.id ?? '')} className="text-error hover:text-error">
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default ContactListPage;
